/*
  BaseService: lớp cơ sở gọi API với hỗ trợ:
  - Xây dựng URL từ host/prefix/version/uri
  - Headers mặc định và ghi đè
  - Params validate sơ bộ (optional)
  - Hỗ trợ phương thức GET/POST/PUT/PATCH/DELETE và DOWNLOAD (blob)
  - Timeout qua AbortController
  - Cache TTL in-memory đơn giản (key, timeout, cacheIn)
*/

import { logger } from '../logger';

export class HttpError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any, message?: string) {
    super(message || (typeof body === 'string' ? body : JSON.stringify(body)));
    this.status = status;
    this.body = body;
  }
}

export type ServiceMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | 'DOWNLOAD';

export interface IProxy {
  host: string;
  port: number;
  protocol: string;
  auth?: {
    username: string;
    password: string;
  };
}

export type ParamType =
  | string
  | number
  | boolean
  | null
  | undefined
  | string[]
  | number[]
  | boolean[];

export interface IParam {
  required?: boolean;
  default?: ParamType;
  transform?: (value: any) => any;
}

export interface IService {
  uri?: string;
  url?: string;
  method: ServiceMethod;
  params?: { [key: string]: IParam | ParamType };
  headers?: { [key: string]: string | null };
  contentType?: 'application/json' | 'text/xml' | string | string[];
  host?: string;
  prefix?: string;
  timeout?: number;
  proxy?: IProxy;
  version?: string;
  authRequire?: boolean | 'optional';
  flatParams?: boolean;
  maxAttempts?: number;
  responseType?: 'text' | 'json' | 'blob' | 'arrayBuffer' | 'formData';
  cache?: {
    key?: string;
    timeout?: number; // seconds
    cacheIn?: number; // not used here, placeholder
  };
}

export type IServices = { [key: string]: IService };

// In-memory cache đơn giản
const memoryCache = new Map<string, { expiresAt: number; data: any }>();

function setCache(key: string, data: any, ttlSeconds: number) {
  if (!ttlSeconds || ttlSeconds <= 0) return;
  const expiresAt = Date.now() + ttlSeconds * 1000;
  memoryCache.set(key, { expiresAt, data });
}

function getCache(key: string): any | undefined {
  const item = memoryCache.get(key);
  if (!item) return undefined;
  if (Date.now() >= item.expiresAt) {
    memoryCache.delete(key);
    return undefined;
  }
  return item.data;
}

function hashKey(input: any): string {
  const str = typeof input === 'string' ? input : JSON.stringify(input);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return `k_${Math.abs(hash)}`;
}

export class BaseService<T = any> implements IService {
  uri?: string | undefined;
  url?: string | undefined;
  method: ServiceMethod = 'GET';
  params?: { [key: string]: IParam | ParamType } | undefined;
  headers?: { [key: string]: string | null } | undefined;
  flatParams: boolean = false;
  flatObjects: boolean = false;
  contentType?: string[] | string | undefined;
  host?: string | undefined;
  prefix?: string | undefined;
  timeout: number = -1;
  proxy?: IProxy | undefined; // placeholder, fetch proxy qua runtime env nếu cần
  version?: string;
  authRequire?: boolean;
  responseType?: 'text' | 'json' | 'blob' | 'arrayBuffer' | 'formData';
  cache?: { key?: string; timeout?: number; cacheIn?: number } | undefined;

  constructor(service: IService, defaults?: { host?: string; prefix?: string }) {
    Object.assign(this, service);
    this.buildUrl({ host: defaults?.host, prefix: defaults?.prefix });
  }

  private buildUrl({ host, prefix }: { host?: string; prefix?: string } = {}) {
    if (this.url) return;
    if (this.uri == null) throw new Error('url or uri is required');

    const parts: (string | undefined)[] = [];
    const hostPart = host || this.host;
    if (hostPart) parts.push(hostPart.replace(/\/$/, ''));
    const prefixPart = this.prefix || prefix;
    if (prefixPart) parts.push(prefixPart.replace(/^\//, '').replace(/\/$/, ''));
    if (this.version) parts.push(this.version.replace(/^\//, '').replace(/\/$/, ''));
    if (this.uri) parts.push(this.uri.replace(/^\//, '').replace(/\/$/, ''));
    this.url = parts.join('/');
  }

  private assignHeaders(headers?: { [key: string]: string }) {
    const httpHeaders: Record<string, string> = {};
    const missing: string[] = [];
    const contentTypeArray = Array.isArray(this.contentType)
      ? (this.contentType as string[])
      : this.contentType
        ? [this.contentType as string]
        : [];
    const contentType = contentTypeArray.filter(Boolean).join('; ');

    this.headers = this.headers || {};
    const merged: Record<string, string | null> = {
      ...this.headers,
      ...(headers || {}),
      'Content-Type':
        contentType || (this.method !== 'GET' ? 'application/json; charset=UTF-8' : ''),
    };

    Object.entries(merged).forEach(([k, v]) => {
      if (v == null) {
        missing.push(k);
      } else if (v !== '') {
        httpHeaders[k] = v;
      }
    });

    if (missing.length) {
      throw new Error(`Missing headers: ${missing.join(', ')}`);
    }

    return httpHeaders;
  }

  // Validate params đơn giản: apply default, transform, required
  private assignParams(params?: { [key: string]: any }) {
    if (!this.params) return {} as Record<string, any>;
    const out: Record<string, any> = {};
    const errors: string[] = [];

    Object.entries(this.params).forEach(([key, def]) => {
      const value =
        params && Object.prototype.hasOwnProperty.call(params, key) ? params[key] : undefined;
      if (def && typeof def === 'object' && !Array.isArray(def)) {
        const p = def as IParam;
        let val = value ?? p.default;
        if (p.required && (val === undefined || val === null || val === '')) {
          errors.push(`${key} is required`);
        }
        if (p.transform && val !== undefined) val = p.transform(val);
        if (val !== undefined) out[key] = val;
      } else if (def !== undefined) {
        out[key] = value ?? def;
      } else if (value !== undefined) {
        out[key] = value;
      }
    });

    if (errors.length) {
      throw new Error(`Validation failed: ${errors.join(', ')}`);
    }

    return out;
  }

  async request(
    options: {
      params?: { [key: string]: any };
      headers?: { [key: string]: string };
      cache?: { disabled?: boolean; key?: string; timeout?: number; cacheIn?: number };
    } = {}
  ): Promise<any> {
    const headers = this.assignHeaders(options.headers);
    const params = this.assignParams(options.params);

    const controller = new AbortController();
    const signal = controller.signal;

    const isServer = typeof window === 'undefined';
    const requestId = `[REQUEST] ${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    let url = (this.url || '').replace(/:([^/:.]+)|\{([^/{}]+)\}/gim, (_, a, b) => {
      const key = a || b;
      const val = params[key];
      if (val === undefined || val === null) return _;
      delete params[key];

      return encodeURIComponent(String(val));
    });

    // Cache
    let cacheKey = '';
    if ((options.cache || this.cache) && !options.cache?.disabled) {
      cacheKey = options.cache?.key || hashKey({ url, method: this.method, params, headers });
      const cached = getCache(cacheKey);
      if (cached !== undefined) return cached;
    }

    const timeoutMs = this.timeout ?? -1;
    const timer = timeoutMs > -1 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

    const init: RequestInit = {
      method: this.method === 'DOWNLOAD' ? 'GET' : this.method,
      headers,
      signal,
    };

    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(this.method)) {
      if (headers['Content-Type'] === 'multipart/form-data') {
        const formData = new FormData();
        Object.entries(params).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach(vi => formData.append(k, vi));
          else if (v != null) formData.append(k, v);
        });
        init.body = formData;
        // Remove Content-Type header to let browser set it with boundary
        delete headers['Content-Type'];
      } else if (headers['Content-Type'] === 'application/x-www-form-urlencoded') {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach(vi => usp.append(k, String(vi)));
          else if (v != null) usp.append(k, String(v));
        });
        init.body = usp.toString();
      } else if (this.flatParams) {
        init.body = JSON.stringify(Object.values(params).flat());
      } else if (this.flatObjects) {
        init.body = JSON.stringify(
          Object.values(params).reduce((acc, curr) => ({ ...acc, ...curr }), {})
        );
      } else {
        init.body = JSON.stringify(params);
      }
    } else {
      // GET-like: append query
      const usp = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (Array.isArray(v)) v.forEach(vi => usp.append(k, String(vi)));
        else if (v != null) usp.append(k, String(v));
      });
      const qs = usp.toString();
      if (qs) url += (url.includes('?') ? '&' : '?') + qs;
    }

    try {
      if (isServer) {
        logger.info(requestId, `[${this.method}] ${url}`);
        if (process.env.NEXT_ENV === 'development') {
          logger.info(requestId, JSON.stringify({ headers }));
          logger.info(requestId, JSON.stringify({ params }));
        }
      }

      const res = await fetch(url, init);
      const contentTypeHeader = res.headers.get('Content-Type') || '';
      const isJson = contentTypeHeader.includes('application/json');

      if (!res.ok) {
        let errBody: any = undefined;
        try {
          errBody = await res.json();
        } catch {
          logger.error(requestId, { errBody });
        }

        if (isServer) {
          logger.error(requestId, `${res.statusText} - ${JSON.stringify(errBody)}`);

          return { success: false, errors: errBody?.errors ?? errBody };
        }

        return { success: false, errors: errBody?.errors ?? errBody };
      }

      if (this.method === 'DOWNLOAD' || this.responseType === 'blob') {
        const blob = await res.blob();
        const result: any = { blob, headers: res.headers };

        return result;
      }

      if (res.status === 204) {
        return { status: true, data: null };
      }

      const data: any = isJson ? await res.json() : await res.text();

      if (isServer && process.env.NODE_ENV === 'development') {
        logger.info(requestId, `${res.statusText} - ${JSON.stringify(data)}`);
      }

      // Cache set
      if ((options.cache || this.cache) && !options.cache?.disabled) {
        const ttl = options.cache?.timeout ?? this.cache?.timeout ?? 0;
        if (ttl && cacheKey) setCache(cacheKey, data, ttl);
      }

      if (timer) clearTimeout(timer);

      return data as T;
    } catch (e: any) {
      if (timer) clearTimeout(timer);
      logger.error(requestId, { e });

      return { status: false, errors: e.errors ?? e };
    }
  }

  generateUrl(
    options: { params?: { [key: string]: any }; headers?: { [key: string]: string } } = {}
  ) {
    this.assignHeaders(options.headers); // validate headers
    if (this.method !== 'GET') return { url: this.url, headers: this.headers };

    const url = new URL(this.url || '');
    const params = this.assignParams(options.params);
    Object.entries(params).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(vi => url.searchParams.append(k, String(vi)));
      else if (v != null) url.searchParams.append(k, String(v));
    });
    return url.toString();
  }
}

export default BaseService;
